import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Edit,
  Trash2,
  Star,
  Image as ImageIcon,
  ExternalLink,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { generateCharacterSlug } from "@/utils/characterSlug";
import { getStorageUrl, STORAGE_BUCKETS } from "@/utils/storageUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface CharacterCardProps {
  character: any;
  onEdit: () => void;
  onDelete: () => void;
}

export const CharacterCard = ({
  character,
  onEdit,
  onDelete,
}: CharacterCardProps) => {
  const [imageError, setImageError] = useState(false);
  const characterSlug = character.slug || generateCharacterSlug(character.name);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published":
        return "bg-green-100 text-green-800 border-green-200";
      case "draft":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "archived":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Get the image URL, handling both storage paths and external URLs
  const getImageUrl = () => {
    if (!character.image_url) return null;
    if (character.image_url.startsWith("http")) return character.image_url;
    return getStorageUrl(STORAGE_BUCKETS.CHARACTER_IMAGES, character.image_url);
  };

  const imageUrl = getImageUrl();

  return (
    <div className="bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow p-4 md:p-6">
      <div className="space-y-4">
        {/* Header with image */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg text-gray-900 truncate">
                {character.name}
              </h3>
              {character.featured && (
                <Star className="h-4 w-4 text-yellow-500 fill-current flex-shrink-0" />
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              <Badge className={getStatusColor(character.status)}>
                {character.status}
              </Badge>
              {character.species && (
                <Badge variant="outline" className="text-xs">
                  {character.species}
                </Badge>
              )}
            </div>

            {character.role && (
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-medium">Role:</span> {character.role}
              </p>
            )}

            {/* Character Slug Display - Now Clickable */}
            <div className="text-xs text-gray-500 mb-2">
              <span className="font-medium">Slug:</span>
              <Link
                to={`/characters/${characterSlug}`}
                target="_blank"
                className="ml-1 text-blue-600 hover:text-blue-800 hover:underline"
              >
                /{characterSlug}
              </Link>
            </div>
          </div>

          {/* Character Image - Updated to fit snuggly */}
          <div className="w-16 h-16 flex-shrink-0 bg-gray-50 rounded-lg border overflow-hidden flex items-center justify-center">
            {imageUrl && !imageError ? (
              <img
                src={imageUrl}
                alt={character.name}
                className="max-h-full max-w-full object-contain"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {character.description && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {character.description}
          </p>
        )}

        {/* Additional Info */}
        <div className="space-y-1 text-xs text-gray-500">
          {character.age_group && <div>Age Group: {character.age_group}</div>}
          {character.teaching_focus && (
            <div>Focus: {character.teaching_focus}</div>
          )}
          <div>
            Created: {new Date(character.created_at).toLocaleDateString()}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="flex-1"
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>

          {character.status === "published" && (
            <Button
              variant="outline"
              size="sm"
              asChild
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Link to={`/characters/${characterSlug}`} target="_blank">
                <ExternalLink className="h-3 w-3" />
              </Link>
            </Button>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Character</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{character.name}"? This
                  action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};
